import React from 'react';
import { motion } from 'framer-motion';
import { 
  Activity, 
  ShieldCheck, 
  CreditCard, 
  Users, 
  Pill, 
  CheckCircle, 
  ArrowRight,
  Stethoscope,
  Microscope,
  MessageSquare,
  PhoneCall
} from 'lucide-react';
import './HospitalManagement.css';
import FAQAccordion from './FAQAccordion';

const features = [
  {
    icon: <Activity size={24} />,
    title: 'OPD & IPD Queue Management',
    desc: 'Streamline token systems, doctor consultation queues, IPD bed allocations, ICU tracking, nursing charts, and digital discharge summaries.'
  },
  {
    icon: <CreditCard size={24} />,
    title: 'Tally & GST Integrated Billing',
    desc: 'Instant invoice generation, insurance/TPAs claim processing, automated Tally ERP sync, and itemized billing for cash & cashless patients.'
  },
  {
    icon: <ShieldCheck size={24} />,
    title: 'NABH & ABDM M1/M2/M3 Compliance',
    desc: '100% compliant with Ayushman Bharat Digital Mission (ABDM), ABHA ID creation, health records linking, and NABH accreditation audit logs.'
  },
  {
    icon: <Pill size={24} />,
    title: 'Pharmacy & Stock Inventory',
    desc: 'Batch tracking, automated expiry date alerts, supplier PO management, barcode scanning, and multi-store pharmacy reorder alerts.'
  },
  {
    icon: <Microscope size={24} />,
    title: 'Pathology & Radiology (LIS / RIS)',
    desc: 'Automated analyzer interfacing, diagnostic report templates, WhatsApp/SMS report dispatch to patients, and DICOM image linking.'
  },
  {
    icon: <Users size={24} />,
    title: 'Doctor Portal & Roster Share',
    desc: 'Consultation fee sharing ledgers, duty shift rosters, mobile doctor portal, digital prescription writer, and referral tracking.'
  }
];

const hmsProcessDetailed = [
  { num: '01', title: 'Hospital Audit & Workflow Specs', desc: 'On-site or virtual analysis of bed capacity, OPD queues, lab machinery, pharmacy counters, and Tally billing requirements.' },
  { num: '02', title: 'System Configuration & Customization', desc: 'Setting up OPD/IPD modules, lab analyzer interfaces, doctor consultation fee sharing, and customizing prescription templates.' },
  { num: '03', title: 'Legacy Patient Data Migration', desc: 'Securely migrating historical patient records, EMR notes, diagnostic history, and pharmacy inventory ledgers.' },
  { num: '04', title: 'On-Site Staff & Doctor Training', desc: 'Hands-on training sessions for receptionists, nurses, pharmacists, lab technicians, accounts team, and senior doctors.' },
  { num: '05', title: 'ABDM Certification & Go-Live', desc: 'Testing ABHA card linking, Tally Prime voucher auto-sync, WhatsApp report dispatch, and smooth hospital go-live.' },
  { num: '06', title: 'NABH Audit Assistance & 24/7 AMC', desc: 'Continuous 24/7 technical support, daily automated backups, and incident log assistance for NABH accreditation audits.' }
];

const modules = [
  'Patient Registration & ABHA ID', 'OPD Token System & Vitals',
  'IPD Admission & Bed Tracker', 'OT & Anesthesia Scheduler',
  'Pharmacy Batch Inventory', 'Pathology Lab Analyzer Sync',
  'Radiology DICOM Report Integration', 'Insurance & TPA Claims',
  'NABH Quality Incident Audit', 'GST Invoice & Tally Sync',
  'Doctor Consultation Commission', 'WhatsApp Test Report Dispatch'
];

const faqsDetailed = [
  {
    q: 'How is Mediway HMS implemented step-by-step in a hospital?',
    a: 'Our deployment follows 6 stages: (1) Workflow Audit, (2) Module Customization, (3) Data Migration, (4) Hands-On Staff Training, (5) ABDM & Tally Go-Live, and (6) NABH Audit Assistance & 24/7 Support.'
  },
  {
    q: 'Is Mediway HMS compliant with Ayushman Bharat (ABDM)?',
    a: 'Yes, Mediway HMS is fully integrated with ABDM (M1, M2, M3 certified workflows), enabling seamless ABHA ID creation, health record linking, and national health stack compliance.'
  },
  {
    q: 'Does the software support NABH accreditation auditing?',
    a: 'Definitely. Mediway maintains immutable audit logs, incident reporting modules, patient safety checklists, and electronic medical records (EMR) designed specifically to meet NABH guidelines.'
  },
  {
    q: 'Can Mediway HMS be customized for small nursing homes in Noida & Delhi NCR?',
    a: 'Yes! We offer modular packages tailored for 10-bed clinics up to 500+ bed multi-specialty hospital networks with on-site setup and 24/7 technical support.'
  },
  {
    q: 'How much does Mediway Hospital Management Software cost in India?',
    a: 'Pricing starts from ₹80,000 for single-clinic/nursing home setups and scales based on bed capacity and optional modules (Pharmacy, LIS, RIS).'
  },
  {
    q: 'Can patient diagnostic reports be sent via WhatsApp automatically?',
    a: 'Yes! Once pathology lab results or doctor prescriptions are finalized, the system automatically dispatches PDF reports to the patient\'s WhatsApp number.'
  },
  {
    q: 'Do you provide on-site technical support during hospital go-live?',
    a: 'Yes, our engineering team provides on-site support during go-live across Noida, Ghaziabad, and Delhi NCR hospitals to ensure zero disruption to patient care.'
  }
];

export default function HospitalManagement() {
  const whatsappUrl = "https://wa.me/918287999236?text=Hi%20Wayug!%20I%20would%20like%20a%20demo%20of%20Mediway%20Hospital%20Management%20Software.";

  return (
    <section className="hms-section">
      <div className="container">
        
        {/* Hero Banner */}
        <motion.div 
          className="hms-hero"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="hms-badge">
            <Stethoscope size={16} /> Mediway NextGen HMS
          </div>
          <h1 className="hms-title">
            Hospital Management Software in <span className="gradient-text">India &amp; Delhi NCR</span>
          </h1>
          <p className="hms-subtitle">
            Enterprise ABDM-ready, NABH-compliant Hospital Information System (HIS) powering clinics, nursing homes, and multi-specialty healthcare networks.
          </p>
          <div style={{ marginTop: '2rem', display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a 
              href={whatsappUrl} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              Book Live Demo <ArrowRight size={16} />
            </a>
            <a href="/contact" className="btn btn-outline">
              Request Pricing Proposal
            </a>
          </div>
        </motion.div>

        {/* Feature Grid */}
        <div className="hms-features-grid">
          {features.map((item, idx) => (
            <motion.div 
              key={item.title}
              className="hms-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
            >
              <div className="hms-card-icon">{item.icon}</div>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* 6-Step Implementation Process */}
        <div style={{ margin: '80px 0' }}>
          <h2 className="text-center" style={{ fontSize: '2.2rem', fontWeight: '800', marginBottom: '12px' }}>
            How Hospital Management Implementation Is Done: <span className="gradient-text">Our 6-Step Process</span>
          </h2>
          <p className="text-center" style={{ color: '#a3a3a3', maxWidth: '700px', margin: '0 auto 40px', fontSize: '1rem', lineHeight: '1.6' }}>
            Here is how we deploy Mediway HMS smoothly across your clinic or multi-specialty hospital with zero downtime.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
            {hmsProcessDetailed.map((step) => (
              <div key={step.num} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '28px' }}>
                <span style={{ fontSize: '2rem', fontWeight: '800', color: '#06b6d4' }}>{step.num}</span>
                <h4 style={{ fontSize: '1.2rem', fontWeight: '700', margin: '12px 0 8px', color: '#fff' }}>{step.title}</h4>
                <p style={{ color: '#a3a3a3', fontSize: '0.9rem', lineHeight: '1.6', margin: 0 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Comprehensive Modules Grid */}
        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', padding: '40px', marginBottom: '60px' }}>
          <h3 style={{ fontSize: '1.8rem', fontWeight: '800', textAlign: 'center', marginBottom: '24px' }}>
            Complete Hospital Modules Included
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
            {modules.map((m) => (
              <div key={m} style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#e5e5e5', fontSize: '0.95rem' }}>
                <CheckCircle size={16} color="#06b6d4" /> {m}
              </div>
            ))}
          </div>
        </div>

        {/* Compliance Box */}
        <motion.div 
          className="hms-compliance-box text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
        >
          <h2 style={{ fontSize: '2rem', fontWeight: '800', marginBottom: '1rem' }}>
            Built for NABH &amp; Ayushman Bharat (ABDM) Standards
          </h2>
          <p style={{ color: '#a3a3a3', maxWidth: '700px', margin: '0 auto 1.5rem', lineHeight: '1.7' }}>
            Ensure your hospital stays compliant with government mandates. Mediway links health records securely with ABHA cards, automates Tally accounting, and provides digital prescription features.
          </p>
          <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap', fontWeight: '600', color: '#06b6d4' }}>
            <span><CheckCircle size={16} style={{ display: 'inline', marginRight: '6px' }} /> ABHA ID Generation</span>
            <span><CheckCircle size={16} style={{ display: 'inline', marginRight: '6px' }} /> EMR &amp; E-Prescription</span>
            <span><CheckCircle size={16} style={{ display: 'inline', marginRight: '6px' }} /> Tally &amp; GST Sync</span>
          </div>
        </motion.div>

        {/* FAQ Section */}
        <FAQAccordion 
          title="Detailed Healthcare HMS FAQs" 
          faqs={faqsDetailed} 
          accentColor="#06b6d4" 
        />

        {/* Bottom High-Converting Contact Banner */}
        <div style={{ background: 'linear-gradient(135deg, rgba(6,182,212,0.15) 0%, rgba(37,99,235,0.15) 100%)', border: '1px solid rgba(6,182,212,0.3)', borderRadius: '24px', padding: '48px', textAlign: 'center' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: '800', color: '#fff', marginBottom: '12px' }}>Modernize Your Hospital Management Today</h2>
          <p style={{ color: '#a3a3a3', maxWidth: '600px', margin: '0 auto 24px', fontSize: '1rem', lineHeight: '1.6' }}>
            Get a personalized demo of Mediway HMS for your clinic or hospital network. On-site implementation available in Delhi NCR.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <MessageSquare size={16} /> Schedule Live Demo via WhatsApp
            </a>
            <a href="/contact" className="btn btn-outline" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <PhoneCall size={16} /> Request Price Quote
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
